import { useDeviceType } from "@dash/mq";
import { useTheme } from "@emotion/react";
import { isSignalsQuery } from "@megarax/features";
import { useState } from "react";
import { MdCheck, MdClose, MdOutlineDrafts, MdOutlineMarkEmailUnread } from "react-icons/md";

import { defaultSignalInstanceKey, signalDocIdFromParts } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { TicketListItemDto } from "@megaron/support-contracts";

type Props = {
  item: TicketListItemDto;
};

export function SupportTicketStripeTile({ item }: Props) {
  const theme = useTheme();
  const { isMobile } = useDeviceType();
  const [isHovered, setIsHovered] = useState(false);

  const readMutation = useClientManager("support").setTicketRead().useMutation({
    onSuccess: (_data, _variables, { queryClient: qc }) => {
      qc.invalidateQueries({ queryKey: ["support", "ticketsList"] });
      qc.invalidateQueries({ queryKey: ["support", "getTicket"] });
      void qc.invalidateQueries({ predicate: isSignalsQuery });
    },
  });
  const signalReadMutation = useClientManager("docs").setSignalRead().useMutation({
    onSuccess: (_data, _variables, { queryClient: qc }) => {
      void qc.invalidateQueries({ predicate: isSignalsQuery });
    },
  });

  const status = item.status.toLowerCase();
  const isClosed = status === "closed";
  const isResolved = status === "resolved";
  const isActive = status === "new" || status === "open";
  const isSeen = item.isRead;
  const isReadChangeable = isActive;
  const unreadTicketSignalDocId = signalDocIdFromParts(
    "unreadTicket",
    item.id,
    defaultSignalInstanceKey("unreadTicket"),
  );

  const getStripeStyles = () => {
    if (isClosed) {
      return { background: theme.colors.border, color: "black" };
    }

    if (isResolved) {
      return { background: theme.colors.success, color: "white" };
    }

    if (isActive) {
      if (!isSeen) {
        return {
          background: theme.colors.primary,
          color: "white",
          "&:hover": !isMobile ? { background: theme.colors.primaryLight, color: theme.colors.primary } : undefined,
        };
      }
      return {
        background: theme.colors.primaryLight,
        color: theme.colors.primary,
        "&:hover": !isMobile ? { background: theme.colors.primary, color: "white" } : undefined,
      };
    }

    return { background: theme.colors.primaryLight, color: theme.colors.primary };
  };

  const stripeStyles = getStripeStyles();
  const showReadUnreadIcons = !isResolved && !isClosed;

  const onMutate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadChangeable || readMutation.isLoading || signalReadMutation.isLoading) return;
    const nextIsRead = !item.isRead;
    readMutation.mutate({ id: item.id, isRead: nextIsRead });
    signalReadMutation.mutate({ docIds: [unreadTicketSignalDocId], isRead: nextIsRead });
  };

  return (
    <div css={{ height: "100%", width: "2rem" }}>
      <button
        type="button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onMutate}
        css={{
          width: "2rem",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          border: "none",
          cursor: isReadChangeable
            ? readMutation.isLoading || signalReadMutation.isLoading
              ? "wait"
              : "pointer"
            : "auto",
          inset: 0,
          transition: "background-color 0.2s, color 0.2s",
          ...stripeStyles,
        }}
      >
        {isClosed && <MdClose size={14} />}
        {isResolved && <MdCheck size={14} />}
        {showReadUnreadIcons &&
          !isSeen &&
          (isHovered && !isMobile ? <MdOutlineDrafts size={14} /> : <MdOutlineMarkEmailUnread size={14} />)}
        {showReadUnreadIcons &&
          isSeen &&
          (isHovered && !isMobile ? <MdOutlineMarkEmailUnread size={14} /> : <MdOutlineDrafts size={14} />)}
      </button>
    </div>
  );
}
